/**
 * ============================================================================
 * CurrencyCombobox — Selector de moneda con autocomplete
 * ============================================================================
 * Lista monedas disponibles desde `useTRM().availableCurrencies` (TRM viva en
 * `trm_historico`). Permite que el usuario tipee y filtre. Muestra un warning
 * sutil si la moneda seleccionada NO tiene TRM (el RPC fallará al calcular).
 * ============================================================================
 */
import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useTRM } from "@/hooks/useTRM";

interface Props {
  value: string;
  onChange: (moneda: string) => void;
  disabled?: boolean;
  className?: string;
  /** Compact size (h-8) for inline use in tables. */
  compact?: boolean;
}

const FALLBACK = ["USD", "COP", "EUR", "CNY", "MXN", "BRL", "GBP"];

export function CurrencyCombobox({
  value,
  onChange,
  disabled,
  className,
  compact = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const { trmMap } = useTRM();

  const available = useMemo(() => {
    const set = new Set<string>([...FALLBACK]);
    Object.keys(trmMap || {}).forEach((c) => set.add(c.toUpperCase()));
    if (value) set.add(value.toUpperCase());
    return Array.from(set).sort();
  }, [trmMap, value]);

  const upper = (value || "USD").toUpperCase();
  const hasRate = upper === "COP" || ((trmMap?.[upper] ?? 0) > 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "justify-between font-mono",
            compact ? "h-8 text-[12px] px-2" : "h-9 text-sm",
            !hasRate && "border-amber-500/50 text-amber-700 dark:text-amber-400",
            className,
          )}
        >
          <span className="flex items-center gap-1 truncate">
            {!hasRate && <AlertTriangle className="h-3 w-3 shrink-0" />}
            {upper}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[180px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar moneda…" className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty>Sin resultados</CommandEmpty>
            <CommandGroup>
              {available.map((code) => {
                const rate = code === "COP" ? 1 : (trmMap?.[code] ?? 0);
                const ok = rate > 0;
                return (
                  <CommandItem
                    key={code}
                    value={code}
                    onSelect={() => {
                      onChange(code);
                      setOpen(false);
                    }}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="flex items-center gap-1.5 font-mono">
                      <Check
                        className={cn(
                          "h-3 w-3",
                          upper === code ? "opacity-100 text-primary" : "opacity-0",
                        )}
                      />
                      {code}
                    </span>
                    <span
                      className={cn(
                        "tabular-nums text-[10px]",
                        ok ? "text-muted-foreground" : "text-amber-600",
                      )}
                    >
                      {ok ? `${rate.toLocaleString("es-CO")} COP` : "sin TRM"}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
